/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  Divider,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { createStyles } from '@mui/styles';
import CancelIcon from '@mui/icons-material/Cancel';

import _ from 'lodash';

import config from '../../config';
import Priority from '../Priority.class';
import PrioritiesService from '../Priorities.api';
import PriorityLanguageCopy from '../PriorityLanguageCopy.class';
import FormValidationsUtils from '../../shared/utils/formValidations.utils';
import PriorityLanguagesForm from './PriorityLanguagesForm';
import PriorityTargetingForm from './PriorityTargetingForm';
import PriorityLifetimeForm from './PriorityLifetimeForm';
import UploadButton from '../../shared/components/UploadButton';
import ImageCropDialog from '../../shared/components/ImageCropDialog';

const { ALL_LANGUAGES } = config;
const { required, childValidation } = FormValidationsUtils;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const MAX_IMAGE_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export default function PriorityUpdateForm({
  onAPISuccess,
  onAPIError,
  spacing,
  initialPriority,
}) {
  // state
  const priorityId = initialPriority ? initialPriority.id : null;
  const [priority, setPriority] = useState(initialPriority || new Priority());
  const [keyword, setKeyword] = useState(initialPriority?.keyword || '');
  const [rank, setRank] = useState(initialPriority?.index || '');
  const [image, setImage] = useState(initialPriority?.image || null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [pendingImageSrc, setPendingImageSrc] = useState(null);
  const [pendingFileName, setPendingFileName] = useState('priority-image');
  const [pendingFileType, setPendingFileType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  // validation
  const [isValidating] = useState(true);
  const [validationErrors, setValidationErrors] = useState({}); // top-level only
  const [areChildrenValid, setAreChildrenValid] = useState({
    // this is sort of duplicated logic - consider triggering each form's validate() on load
    languages: _.some(initialPriority?.languages),
    targeting: true,
    lifetime: _.some(initialPriority?.lifetime),
  });
  const styles = createStyles({
    divider: {
      margin: '20px 0',
    },
  });

  function validate() {
    const newErrors = {};

    if (isValidating) {
      if (!required().isValid(keyword)) {
        newErrors.keyword = required().helperText;
      }

      if (!required().isValid(image)) {
        newErrors.image = 'The image is required.';
      }

      const hasErrors = !_.isEmpty(newErrors);
      if (hasErrors || !childValidation().isValid(areChildrenValid)) {
        newErrors.summary = childValidation().helperText;
      }
    }

    setValidationErrors(newErrors);
    return _.isEmpty(newErrors);
  }
  useEffect(() => {
    validate();
  }, [keyword, image, priority]);

  function updateChild(key, newData, isValid) {
    setPriority({ ...priority, [key]: newData });
    setAreChildrenValid({ ...areChildrenValid, [key]: isValid });
  }

  // saving
  function sanitizeAndFormatCurrentState() {
    const nonEmptyLanguages = PriorityLanguageCopy.filterEmptyLanguages(
      priority.languages
    );

    const criteriaToTrim = ['states', 'markets', 'regions', 'storeNumbers'];
    const trimmedTargeting = {
      ...priority.targeting,
      ...criteriaToTrim.reduce(
        (acc, key) =>
          priority.targeting[key]
            ? {
                ...acc,
                [key]: priority.targeting[key].map((str) => str.trim()),
              }
            : acc,
        {}
      ),
    };

    const keywordNoTrailingComma = keyword.replace(/,+$/, '');

    return new Priority(
      priorityId,
      nonEmptyLanguages,
      trimmedTargeting,
      priority.lifetime,
      keywordNoTrailingComma,
      priority.index,
      image
    );
  }

  const handleFileSelected = (event) => {
    const [file] = event.target.files;
    if (!file) {
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageUploadError('Please upload a JPEG or PNG image.');
      return;
    }
    if (file.size > MAX_IMAGE_FILE_SIZE_BYTES) {
      setImageUploadError('Image must be smaller than 10MB.');
      return;
    }
    setImageUploadError(null);
    const baseFileName = file.name.replace(/\.[^./]+$/, '');
    setPendingFileName(baseFileName);
    setPendingImageSrc(URL.createObjectURL(file));
    setPendingFileType(file.type);
    setCropDialogOpen(true);
  };

  const closeCropDialog = () => {
    if (pendingImageSrc) {
      URL.revokeObjectURL(pendingImageSrc);
    }
    setPendingImageSrc(null);
    setCropDialogOpen(false);
  };

  const handleCropComplete = async (croppedFile) => {
    closeCropDialog();
    setImageUploading(true);
    setImageUploadError(null);
    try {
      const fileUrl = await PrioritiesService.uploadPriorityFile(croppedFile);
      setImage(fileUrl);
    } catch (err) {
      setImageUploadError('Failed to upload image. Please try again.');
    }
    setImageUploading(false);
  };

  function saveToAPI() {
    setIsSubmitting(true);
    setApiError(null);

    const finalPriority = sanitizeAndFormatCurrentState();
    PrioritiesService.updateOrCreatePriority(finalPriority)
      .then(onAPISuccess)
      .catch((err) => {
        setApiError(err);
        setIsSubmitting(false);

        onAPIError(err);
      });
  }

  return (
    <Box>
      <Divider sx={styles.divider} />
      <Grid container id="languageSpecific" spacing={spacing}>
        <PriorityLanguagesForm
          initialLanguages={initialPriority?.languages}
          allowedLanguages={ALL_LANGUAGES}
          onStateChanged={(newState, isValid) =>
            updateChild('languages', newState, isValid)
          }
          spacing={spacing}
          isValidating={isValidating}
        />
      </Grid>
      <Divider sx={styles.divider} />
      <Grid container id="targeting" spacing={spacing}>
        <PriorityTargetingForm
          initialTargeting={initialPriority?.targeting}
          onStateChanged={(newState, isValid) =>
            updateChild('targeting', newState, isValid)
          }
          spacing={spacing}
          isValidating={isValidating}
        />
      </Grid>
      <Divider sx={styles.divider} />
      <Grid container id="keywords" spacing={spacing}>
        <Grid item xs={12}>
          <Typography variant="h5">Keywords</Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Keywords (comma separated)"
            fullWidth
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            error={_.has(validationErrors, 'keyword')}
            helperText={validationErrors.keyword}
          />
        </Grid>
      </Grid>
      <Divider sx={styles.divider} />
      <Grid container id="image" spacing={spacing}>
        <Grid item xs={12}>
          <Typography variant="h5">Image</Typography>
          <Typography variant="body2" color="textSecondary">
            JPEG or PNG only. The image will be compressed automatically.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {image && (
            <Box style={{ position: 'relative', width: '110px' }}>
              <img
                src={image}
                alt="Priority"
                style={{
                  width: '110px',
                  height: '110px',
                  objectFit: 'contain',
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                }}
              />
              <IconButton
                style={{ position: 'absolute', top: 0, right: 0 }}
                onClick={() => setImage(null)}
              >
                <CancelIcon style={{ width: '20px' }} />
              </IconButton>
            </Box>
          )}
          <UploadButton
            handleFileUpload={handleFileSelected}
            text={image ? 'Replace image' : 'Upload image'}
            accept=".jpg,.jpeg,.png"
            style={{ minHeight: 0 }}
            buttonStyle={{ width: 'auto', margin: '10px 0' }}
            disabled={imageUploading}
          />
          {imageUploading && (
            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CircularProgress size={20} />
              <Typography>Uploading image...</Typography>
            </Box>
          )}
          {imageUploadError && (
            <Typography color="error">{imageUploadError}</Typography>
          )}
          {validationErrors.image && (
            <Typography color="error">{validationErrors.image}</Typography>
          )}
          <ImageCropDialog
            open={cropDialogOpen}
            imageSrc={pendingImageSrc}
            fileName={pendingFileName}
            fileType={pendingFileType}
            onCancel={closeCropDialog}
            onCropComplete={handleCropComplete}
          />
        </Grid>
      </Grid>
      <Divider sx={styles.divider} />
      <Grid container id="lifetime" spacing={spacing}>
        <PriorityLifetimeForm
          initialLifetime={{
            startDate: initialPriority?.startDate,
            endDate: initialPriority?.endDate,
          }}
          onStateChanged={(newState, isValid) =>
            updateChild('lifetime', newState, isValid)
          }
          spacing={spacing}
          isValidating={isValidating}
        />
      </Grid>
      <Divider sx={styles.divider} />
      <Grid container id="rank" spacing={spacing}>
        <Grid item xs={12}>
          <Typography variant="h5">Rank</Typography>
        </Grid>
        <Grid item xs={12}>
          <Select
            fullWidth
            value={rank}
            onChange={(e) => {
              const { value } = e.target;
              setRank(value);
              updateChild('index', value === '' ? null : value, true);
            }}
            displayEmpty
            inputProps={{ 'aria-label': 'Rank' }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {[...Array(10)].map((__, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {i + 1}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Divider sx={styles.divider} />
      <Grid container id="actions" spacing={spacing}>
        {_.has(validationErrors, 'summary') && (
          <Grid item xs={12}>
            <Typography color="error">{validationErrors.summary}</Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            type="button"
            variant="contained"
            fullWidth
            color="primary"
            size="large"
            onClick={() => saveToAPI()}
            disabled={
              isSubmitting || imageUploading || !_.isEmpty(validationErrors)
            }
          >
            Confirm & {initialPriority ? 'Update' : 'Create'}
          </Button>
        </Grid>

        {apiError && (
          <Grid item xs={12}>
            <Typography color="error">
              An error occurred during submission: &quot;{apiError.message}
              &quot; {apiError.status && `(status ${apiError.status})`}
            </Typography>
            <Typography color="error">
              Please try again. If the issue persists, please contact MLS
              support.
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

PriorityUpdateForm.propTypes = {
  initialPriority: PropTypes.instanceOf(Priority),
  onAPISuccess: PropTypes.func,
  onAPIError: PropTypes.func,
  spacing: PropTypes.number,
};

PriorityUpdateForm.defaultProps = {
  initialPriority: null,
  onAPISuccess: () => {},
  onAPIError: () => {},
  spacing: 1,
};
